/**
 * DIE TEILEN-KARTE — was beim Verschicken eines Scrolly-Links auf der
 * Vorschau steht (WhatsApp, LinkedIn, X, iMessage …) und was Google als
 * Titel und Beschreibung liest.
 *
 * EINE Regel fuer zwei Stellen:
 *   • der Viewer schreibt daraus `<title>`, `og:title`, `og:description`,
 *     `og:image` (public/src/components/viewer/ScrollyMetaTags.tsx)
 *   • der Template-Editor zeigt daraus die Vorschau im Reiter „Social Preview"
 *     (app/…/TemplateEditor/SocialPreviewPanel.tsx)
 *
 * Jans Fund 02.10.2026: Die beiden Stellen hatten je eigene Regeln. Der Editor
 * zeigte die UEBERSCHRIFT (`metadata.title`), der Viewer verschickte aber den
 * DATEINAMEN (`title` an der Wurzel — der interne Arbeitsname aus dem Feld
 * „Dateiname · nur intern"). Gemessen am Welcome-Scrolly auf prod: geteilt
 * wurde „Welcome Scrolly (DE)" statt „Dürfen wir dir etwas Aufmerksamkeit
 * schenken?". Die Unterzeile las der Viewer an der Wurzel, wo sie nie steht
 * (`metadata.subtitle`) — statt „90 Sekunden!" ging ein Satzfetzen der ersten
 * Slide hinaus, oder der allgemeine Satz ueber Scrolly.
 *
 * Reine Funktionen ohne React und ohne DOM, damit beide Seiten sie benutzen
 * und Vitest sie ohne Browser pruefen kann.
 */

/** Ziel-Laenge fuer meta description / og:description. */
export const TEILEN_BESCHREIBUNG_MAX = 160;

/** Ohne eigenen Titel (gibt es praktisch nur bei „nicht gefunden"). */
export const TEILEN_RUECKFALL_TITEL =
  'Scrolly — Die Heimat scrollbarer Inhalte';

/** HTML-Tags entfernen, Entities aufloesen, Whitespace zusammenziehen. */
export function textOhneHtml(input: string): string {
  return input
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Auf max Zeichen kuerzen, an Wortgrenze, mit Ellipse. */
export function kuerzeAnWortgrenze(
  text: string,
  max = TEILEN_BESCHREIBUNG_MAX,
): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${base.replace(/[\s,;:.\-–]+$/, '')}…`;
}

/**
 * Der Teil eines Scrollys, den die Karte braucht — bewusst locker getypt:
 * Bestand (PPI-Importe, alte Studio-Scrollys) traegt Felder an Stellen, die
 * der strenge Typ nicht kennt.
 */
export interface TeilenQuelle {
  /** Dateiname / Arbeitsname (Feld „Dateiname · nur intern"). */
  title?: unknown;
  description?: unknown;
  /** Nur Altbestand — die Unterzeile steht sonst in `metadata.subtitle`. */
  subtitle?: unknown;
  metadata?: {
    /** Ueberschrift des Startbildschirms — das, was der Leser sieht. */
    title?: unknown;
    /** Unterzeile des Startbildschirms. */
    subtitle?: unknown;
    description?: unknown;
    titleImage?: { url?: unknown } | null;
    thumbnailResized?: { url?: unknown } | null;
    thumbnail?: unknown;
  } | null;
  data?: {
    slides?: ReadonlyArray<
      { text?: { text?: unknown } | null } | null | undefined
    > | null;
    layers?: ReadonlyArray<unknown> | null;
  } | null;
  /** Legacy: Ebenen an der Wurzel. */
  layers?: ReadonlyArray<unknown> | null;
}

const sauber = (wert: unknown): string =>
  typeof wert === 'string' ? textOhneHtml(wert) : '';

/**
 * TITEL der Karte: die UEBERSCHRIFT des Startbildschirms. Der Dateiname ist
 * nur der Rueckfall fuer Scrollys ohne Ueberschrift — er ist der interne
 * Arbeitsname („… – Version 2", „Test …") und gehoert nicht auf eine Karte,
 * die jemand verschickt.
 */
export function teilenTitel(
  quelle: TeilenQuelle | null | undefined,
): string | undefined {
  if (!quelle) return undefined;
  return sauber(quelle.metadata?.title) || sauber(quelle.title) || undefined;
}

/**
 * BESCHREIBUNG der Karte, in dieser Reihenfolge:
 *
 *   1. die ausdrueckliche Beschreibung (Reiter „Social Preview")
 *   2. die Unterzeile des Startbildschirms
 *   3. der erste Slide-Text mit mindestens 20 Zeichen
 *   4. Notnagel: der laengste kurze Slide-Text
 *
 * Was der AUTOR gesetzt hat (1 und 2), gilt immer — auch wenn es kurz ist.
 * Die 20-Zeichen-Grenze gilt nur fuer das GERATENE (Slide-Texte): ein
 * einzelnes Wort aus irgendeiner Slide ist keine Beschreibung, solange es
 * irgendwo einen laengeren Text gibt.
 *
 * Liefert `undefined`, wenn nichts da ist. Dann steht auf der Karte KEINE
 * Beschreibung — weder im Viewer noch in der Vorschau (Jans Entscheidung
 * 02.10.2026). Vorher stand dort der allgemeine Satz ueber Scrolly
 * („Datenstorys, Erklärstücke, …"), der mit dem verschickten Scrolly nichts
 * zu tun hat.
 */
export function teilenBeschreibung(
  quelle: TeilenQuelle | null | undefined,
): string | undefined {
  if (!quelle) return undefined;
  const vomAutor = [
    quelle.metadata?.description,
    quelle.description,
    quelle.metadata?.subtitle,
    quelle.subtitle,
  ];
  for (const roh of vomAutor) {
    const text = sauber(roh);
    if (text) return kuerzeAnWortgrenze(text);
  }
  const slides: ReadonlyArray<
    { text?: { text?: unknown } | null } | null | undefined
  > = Array.isArray(quelle.data?.slides) ? quelle.data.slides : [];
  const texte = slides
    .map((slide) => sauber(slide?.text?.text))
    .filter((t) => t.length > 0);
  const lang = texte.find((t) => t.length >= 20);
  if (lang) return kuerzeAnWortgrenze(lang);
  if (texte.length === 0) return undefined;
  return kuerzeAnWortgrenze([...texte].sort((a, b) => b.length - a.length)[0]);
}

/**
 * Erste Bild-Ebene (content.url) aus dem Scrolly-JSON, oder undefined.
 * Vorrang hat das erste EIGENE Bild (Nutzer-Upload unter assets/…) vor
 * Template-Deko (template-assets/…) — sonst gewaenne die Cover-Collage einer
 * nicht ersetzten Titelfolie statt des eigenen Fotos.
 *
 * (Aus public/src/lib/first-image.ts hierher gezogen, damit die Vorschau im
 * Editor denselben Rueckfall kennt wie der Viewer.)
 */
export function erstesBildDerEbenen(json: unknown): string | undefined {
  if (!json || typeof json !== 'object') return undefined;
  const j = json as TeilenQuelle;
  const ebenen: ReadonlyArray<unknown> = Array.isArray(j.data?.layers)
    ? j.data.layers
    : Array.isArray(j.layers)
      ? j.layers
      : [];
  let templateRueckfall: string | undefined;
  for (const roh of ebenen) {
    const ebene = roh as {
      type?: unknown;
      content?: { url?: unknown } | null;
    } | null;
    if (!ebene || ebene.type !== 'image' || !ebene.content) continue;
    const url =
      typeof ebene.content.url === 'string' ? ebene.content.url.trim() : '';
    if (!url) continue; // nur content.url ist direkt als Bild nutzbar
    if (!/template-assets\//.test(url)) return url; // erstes eigenes Foto gewinnt
    if (!templateRueckfall) templateRueckfall = url; // Notnagel merken
  }
  return templateRueckfall;
}

const adresse = (wert: unknown): string | undefined =>
  typeof wert === 'string' && wert.trim() ? wert.trim() : undefined;

/**
 * Welches Bild auf die Karte kommt (Adresse VOR dem Zuschnitt):
 * Titelbild (Altbestand) → Social-Preview-Bild → Vorschaubild (Kachel) →
 * erstes Bild des Scrollys.
 */
export function teilenBildQuelle(
  quelle: TeilenQuelle | null | undefined,
): string | undefined {
  if (!quelle) return undefined;
  const meta = quelle.metadata;
  return (
    adresse(meta?.titleImage?.url) ??
    adresse(meta?.thumbnailResized?.url) ??
    adresse(meta?.thumbnail) ??
    erstesBildDerEbenen(quelle)
  );
}

/** Die Karte ist 1200×630 (1,91 : 1) — das Mass, bei dem die grosse Vorschau greift. */
const KARTEN_ZUSCHNITT = 'rs:fill:1200:630';

/**
 * Eine Bild-Adresse auf das Karten-Bild umschreiben (nur das Bild, kein Text).
 * - /img-Adresse → Verarbeitung auf rs:fill:1200:630 tauschen, als JPEG
 *   (WebP ist als og:image bei WhatsApp/LinkedIn unzuverlaessig)
 * - andere Adresse → unveraendert (kein Zuschnitt moeglich)
 *
 * ACHTUNG, gemessen 02.10.2026: Der Bildserver vergroessert nicht
 * (assets.controller, `withoutEnlargement`). Ein Bild, das schmaler als 1200
 * oder niedriger als 630 ist, kommt deshalb NICHT als 1,91 : 1 heraus — aus
 * 640×640 wird 640×630. Wer wissen will, wie die Karte wirklich aussieht,
 * muss also genau DIESE Adresse laden (so macht es die Editor-Vorschau).
 *
 * (Aus public/src/lib/first-image.ts hierher gezogen.)
 */
export function alsTeilenBild(url: string | undefined): string | undefined {
  if (!url) return undefined;
  const imgIdx = url.indexOf('/img/');
  const plainIdx = url.indexOf('/plain/');
  if (imgIdx >= 0 && plainIdx > imgIdx) {
    const origin = url.slice(0, imgIdx);
    const keyPart = url.slice(plainIdx + '/plain/'.length).replace(/@\w+$/, '');
    return `${origin}/img/${KARTEN_ZUSCHNITT}/plain/${keyPart}@jpg`;
  }
  return url;
}

/** BILD der Karte — fertige Adresse, wie sie in `og:image` steht. */
export function teilenBild(
  quelle: TeilenQuelle | null | undefined,
): string | undefined {
  return alsTeilenBild(teilenBildQuelle(quelle));
}
